import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Icon from 'components/icon';
import t from 'translations';
import { connect } from 'react-redux';
import reduxUtils from 'util/redux';
import { debounce, noop } from 'lodash';
import { subscribe, unsubscribe } from 'util/misc/overlay-tracker';
import { add as addEvent, remove as removeEvent } from 'bianco.events';
import { TOOLTIP_HOVER_CLOSE_DEBOUNCE, TOOLTIP_HOVER_INTENT_DEBOUNCE } from 'constants/debounce-delays';
import stopPropagation from 'util/dom/stop-propagation';
import preventDefault from 'util/dom/prevent-default';
import mapFacets from 'util/react/facet';
import ButtonsGroup from 'components/buttons-group';
import Aria51 from 'components/aria-51';


class Tooltip extends PureComponent {
    static defaultProps = {
        facet: 'base',
        isVisible: false,
        hasClose: false,
        actions: [],
        lateralOffset: 20,
        xInnerOffset: 0,
        onClose: noop,
        position: {},
        interactionType: 'hover',
        forceOpen: null,
        onRef: noop,
    };

    static propTypes = {
        facet: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        mustShow: PropTypes.bool,
        forcePosition: PropTypes.string,
        actions: PropTypes.array,
        lateralOffset: PropTypes.number,
        timeout: PropTypes.number,
        className: PropTypes.string,
        onShow: PropTypes.func,
        onHide: PropTypes.func,
        position: PropTypes.object,
        interactionType: PropTypes.oneOf(['click', 'hover', 'none']), // select "none" to disable interaction
        areBlocked: PropTypes.bool.isRequired,
        setBlocked: PropTypes.func.isRequired,
        headerHeight: PropTypes.number.isRequired,
        viewportWidth: PropTypes.number.isRequired,
        viewportHeight: PropTypes.number.isRequired,
        content: PropTypes.any.isRequired,
        hasClose: PropTypes.bool,
        onClose: PropTypes.func,
        hasTouch: PropTypes.bool.isRequired,
        children: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]).isRequired,
        forceOpen: PropTypes.bool,
        onRef: PropTypes.func,
    };

    state = {
        xPlacement: 'center', // either "center", "left" or "right"
        yPlacement: 'top', // "top" or "bottom"
        isActive: false,
    };

    componentDidMount() {
        // https://github.com/kriasoft/react-starter-kit/issues/909#issuecomment-252969542
        this.props.onRef(this);
        this.setPosition(this.props);
    }


    componentWillReceiveProps({ forceOpen }) {
        if (forceOpen !== this.props.forceOpen && forceOpen === true) {
            this.show();
        }
    }

    componentDidUpdate({ mustShow }) {
        if (this.props.mustShow && !mustShow) {
            this.setPosition(this.props);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
        removeEvent(document.body, 'click', this.onBodyClick);
        this.show.cancel();
    }


    /**
     * Hide the tooltip if users click anywhere in the DOM
     */
    onBodyClick = (e) => {
        const { root } = this.refs;
        if (root.contains(e.target)) {
            return;
        }

        if (this.props.timeout) {
            clearTimeout(this.timer);
        }

        this.hide();
    };

    /**
     * Handle the click events on the tooltips
     * @param {object} e - event object
     */
    onClick = (e) => {
        if ((this.props.interactionType !== 'click' && !this.props.hasTouch) || this.props.interactionType === 'none') {
            return;
        }

        preventDefault(e);

        this.show();
    };

    /**
     * Show tooltips only for the components having the attribute interaction type == 'hover'
     */
    onMouseEnter = () => {
        if (this.props.interactionType !== 'hover') {
            return;
        }

        this.show.cancel(); // Cancel the current show
        this.onMouseLeave.cancel(); // But also delay the leave
        this.show();
    };

    /**
     * Hide the tooltips on mouse leave for the tooltips having the attribute interaction type == 'hover'
     */
    onMouseLeave = debounce(function() {
        if (this.props.interactionType !== 'hover' || this.props.timeout) {
            return;
        }

        this.hide();
    }, TOOLTIP_HOVER_CLOSE_DEBOUNCE);

    /**
     * Calculate the tooltip position
     */
    setPosition = ({ viewportWidth, headerHeight }) => {
        const { right, left, height } = this.refs.tooltip.getBoundingClientRect();
        const { top: rootTop, left: rootLeft } = this.refs.root.getBoundingClientRect();
        const { lateralOffset, forcePosition, position } = this.props;
        let xPlacement = 'center';
        let yPlacement = 'top';
        let xInnerOffset = 0;

        if (forcePosition === 'left') {
            xPlacement = 'left';
            xInnerOffset = rootLeft - left;
        } else if (left < lateralOffset) {
            xPlacement = 'left';
            xInnerOffset = rootLeft - left;
        } else if (right + lateralOffset > viewportWidth) {
            xPlacement = 'right';
            xInnerOffset = viewportWidth - (right + lateralOffset);
        }

        if (rootTop - height - headerHeight < 0) {
            yPlacement = 'bottom';
        }

        if (xPlacement === 'left') {
            xInnerOffset = Math.abs(xInnerOffset);
        }

        this.setState({
            xInnerOffset: xInnerOffset,
            yPlacement: position.y || yPlacement,
            xPlacement: position.x || xPlacement,
        });
    };

    /**
     * Hide the tooltip if previously shown
     */
    hide = () => {
        if (!this.state.isActive && !this.props.mustShow) {
            return;
        }

        unsubscribe(this);
        this.props.onClose();

        removeEvent(document.body, 'click', this.onBodyClick);

        this.show.cancel();
        this.setState({
            isActive: false,
        });

        // Trigger callback if provided
        this.props.onHide && this.props.onHide();

        if (this.props.timeout) {
            this.props.setBlocked({ areBlocked: false });
        }
    };


    /**
     * Tooltip toggle function debounced because we want to avoid a too nervous user interaction
     * @type {Function}
     */
    show = debounce(function() {
        // If it is active, abort
        // If a another tooltip with a delay is active, abort
        if (this.state.isActive || this.props.areBlocked) {
            return;
        }

        subscribe(this, this.hide);

        // React syntetic events do not allow stopPropagation so we are forced to use vanilla events
        addEvent(document.body, 'click', this.onBodyClick);

        this.setPosition(this.props);
        this.setState({
            isActive: true,
        });

        // Trigger callback if provided
        this.props.onShow && this.props.onShow();

        if (this.props.timeout) {
            clearTimeout(this.timer);
            this.timer = setTimeout(this.hide, this.props.timeout);
            // Block all other tooltip interactions for the duration of the current tooltip
            this.props.setBlocked({ areBlocked: true });
        }
    }, TOOLTIP_HOVER_INTENT_DEBOUNCE);

    timer = null;

    triggerEvent(eventData, event) {
        if (typeof event === 'function') {
            event();
        }

        stopPropagation(eventData);
        this.hide();
    }

    renderActionsButtons(actions) {
        const buttonsData = actions.map(({ name, event, isHighlighted }) => {
            return {
                size: 'small',
                className: classnames('tooltip--button', { 'tooltip--button__is-highlighted': isHighlighted }),
                name,
                event: (e) => this.triggerEvent(e, event),
            };
        });

        return <ButtonsGroup actions={ buttonsData } className="tooltip--buttons-group"/>;
    }

    renderCloseButton() {
        return (
            <button aria-label={ t('accessibility.label.close_tooltip') } className="tooltip--close"
                onClick={ (e) => preventDefault(e) || stopPropagation(e) && this.hide() }>
                <Icon symbol="cancel-small" size="medium"/>
            </button>
        );
    }

    render() {
        const { facet, children, mustShow, content, actions, className } = this.props;
        const { isActive, yPlacement, xPlacement, xInnerOffset } = this.state;
        const hasClose = this.props.interactionType === 'click' || this.props.hasClose;

        return (
            <div
                ref="root"
                onClick={ this.onClick }
                onMouseLeave={ () => this.onMouseLeave() }
                onMouseEnter={ () => this.onMouseEnter() }
                className={ classnames('tooltip', mapFacets(facet, 'tooltip'), className) }>
                <div
                    ref="tooltip"
                    className={ classnames(
                        'tooltip--container',
                        `tooltip--container__${ yPlacement }`,
                        `tooltip--container__${ xPlacement }`,
                        { 'tooltip--container__is-active': isActive || mustShow },
                    ) }>
                    <span className="tooltip--arrow"/>
                    <Aria51
                        tabIndex={ isActive ? 0 : -1 }
                        role="document"
                        aria-modal="true"
                        aria-hidden={ !isActive }
                        isActive={ isActive }
                        className={ classnames('tooltip--inner', { 'tooltip--inner__has-close': hasClose }) }
                        style={ { transform: `translate(${ xInnerOffset }px, 0)` } }>
                        { hasClose ? this.renderCloseButton() : null }
                        { content }
                        { actions.length ? this.renderActionsButtons(actions) : null }
                    </Aria51>
                </div>
                <div className="tooltip--content">
                    { children }
                </div>
            </div>
        );
    }
}


export default connect(
    reduxUtils.mapStateToProps({
        headerHeight: 'header.height',
        viewportHeight: 'viewport.height',
        viewportWidth: 'viewport.width',
        hasTouch: 'viewport.touchevents',
        areBlocked: 'tooltip.areBlocked',
    }),
    reduxUtils.mapDispatchToProps({
        setBlocked: 'tooltip.setBlocked',
    }),
)(Tooltip);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/tooltip/index.js